import { useEffect, useState, useCallback, useRef } from 'react';
import { api } from '../api';
import { useApp } from '../contexts/AppContext';
import useHelpContextSignal from '../hooks/useHelpContextSignal';
import useHelpTarget from '../hooks/useHelpTarget';

function formatElapsed(seconds) {
  if (!seconds || seconds < 1) return '';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

/**
 * TierScopePrompt — the "Document Readiness" cost preview shown above the
 * Ask Arc chat composer when the user has a scoped doc set.
 *
 * Surfaces the tier breakdown (skim / surface / deep) for the scope and
 * offers one-click upgrade to surface or deep search before the user
 * starts a conversation, so they understand whether Arc is searching
 * sample pages or every page — and what the upgrade costs.
 *
 * When a deep upgrade dispatches a background job, the prompt itself
 * follows the job through to completion: buttons stay disabled, the
 * deep-search button morphs into a live "upgrading N docs · 1m 30s"
 * indicator, and on completion the breakdown re-fetches so the prompt
 * either shrinks or disappears. This keeps the user from re-clicking
 * the button mid-job and from having to spot the separate
 * BackgroundJobsBanner row to know what's happening.
 */
export default function TierScopePrompt({ caseId }) {
  const { chatScope } = useApp();
  const helpRef = useHelpTarget({
    slug: 'tier-upgrades-from-arc',
    label: 'Tier upgrade prompt',
    question: 'What does this tier upgrade card mean and when should I accept it?',
  });
  const [breakdown, setBreakdown] = useState(null);
  const [loading, setLoading] = useState(false);
  const [upgrading, setUpgrading] = useState(null);   // 'surface' | 'deep'
  const [msg, setMsg] = useState(null);

  // When upgrade-tier returns mode='background', we hand off to a job
  // poller that drives the button label and re-fetches the breakdown
  // on completion. Lifecycle: queued/processing → completed (auto-clear
  // after a brief ✓ moment) or failed (sticks so user can retry).
  const [bgJob, setBgJob] = useState(null);
  // Shape: { id, tier, status, totalDocs, completed, elapsed, error }

  // Synchronous guard against rapid double-click. The state-based
  // `if (upgrading || bgJob) return` below reads from the closure of
  // its render — two clicks in the same React tick both see the same
  // stale state and both pass. The ref check fires synchronously so
  // the second click bails before any duplicate POST.
  const upgradeInFlightRef = useRef(false);

  const docIds = chatScope?.document_ids || [];

  const refetchBreakdown = useCallback(async () => {
    if (!docIds.length) {
      setBreakdown(null);
      return;
    }
    try {
      const data = await api.post(
        `/exploration/case/${caseId}/tier-breakdown`,
        { document_ids: docIds, complete_extraction: false },
      );
      setBreakdown(data);
    } catch {
      // best-effort
    }
  }, [caseId, JSON.stringify(docIds)]);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!docIds.length) {
        setBreakdown(null);
        return;
      }
      setLoading(true);
      try {
        const data = await api.post(
          `/exploration/case/${caseId}/tier-breakdown`,
          { document_ids: docIds, complete_extraction: false },
        );
        if (!cancelled) setBreakdown(data);
      } catch {
        if (!cancelled) setBreakdown(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [caseId, JSON.stringify(docIds)]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Reset transient upgrade state when the scope itself changes — the
  // user is now looking at a different doc set, so the in-flight state
  // for the prior scope is no longer relevant. The job keeps running
  // in the background and remains visible via BackgroundJobsBanner.
  useEffect(() => {
    setBgJob(null);
    setUpgrading(null);
    setMsg(null);
  }, [JSON.stringify(docIds)]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Poll the case's background jobs while a deep upgrade is in flight.
  // Reuses the same endpoint BackgroundJobsBanner uses; matches by
  // job_id so we only react to the job we dispatched.
  useEffect(() => {
    if (!bgJob || !bgJob.id) return;
    if (bgJob.status === 'completed' || bgJob.status === 'failed') return;
    let cancelled = false;
    async function poll() {
      try {
        const data = await api.get(`/exploration/case/${caseId}/background-jobs`);
        if (cancelled) return;
        const job = (data.jobs || []).find(j => j.job_id === bgJob.id);
        if (!job) {
          // The endpoint stops returning a job ~60s after it terminates.
          // Missing => assume completed; refetchBreakdown effect handles
          // the rest. (We poll at 5s, well inside the 60s window.)
          setBgJob(prev => prev ? { ...prev, status: 'completed' } : prev);
          return;
        }
        const p = job.progress || {};
        setBgJob(prev => prev ? {
          ...prev,
          status: job.status,
          totalDocs: p.total_documents ?? prev.totalDocs,
          completed: p.documents_completed,
          elapsed: job.elapsed_seconds || 0,
          error: job.error,
        } : prev);
      } catch {
        // non-fatal
      }
    }
    poll();
    const timer = setInterval(poll, 5000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [caseId, bgJob?.id, bgJob?.status]);

  // Job lifecycle landing — runs when status flips to terminal state.
  // On success: refetch breakdown (likely hides the prompt) and auto-
  // clear after a beat so the user sees a "ready" moment first.
  // On failure: re-enable buttons so the user can retry; keep the
  // error message visible until they act.
  useEffect(() => {
    if (!bgJob) return;
    if (bgJob.status === 'completed') {
      refetchBreakdown();
      const t = setTimeout(() => {
        setBgJob(null);
        setUpgrading(null);
        setMsg(null);
        upgradeInFlightRef.current = false;
      }, 4000);
      return () => clearTimeout(t);
    }
    if (bgJob.status === 'failed') {
      setUpgrading(null);
      upgradeInFlightRef.current = false;
    }
  }, [bgJob?.status, refetchBreakdown]);

  // Auto-dismiss inline success messages (failures stick).
  useEffect(() => {
    if (!msg || msg.error) return;
    const t = setTimeout(() => setMsg(null), 4000);
    return () => clearTimeout(t);
  }, [msg]);

  // Compute visibility. The prompt is visible iff we have a real
  // breakdown for a real scope and at least one doc would benefit
  // from upgrading — OR a background upgrade we dispatched is still
  // mid-flight or in its brief "ready/failed" landing window.
  const skim = breakdown?.skim || 0;
  const surface = breakdown?.surface || 0;
  const deep = breakdown?.deep || 0;
  const total = breakdown?.total || 0;
  const surfaceCost = breakdown?.surface_upgrade_cost_usd || 0;
  const deepCost = breakdown?.deep_upgrade_cost_usd || 0;
  const isVisible = Boolean(
    docIds.length && !loading && breakdown && (deep !== total || bgJob),
  );
  useHelpContextSignal('tier_upgrade_prompt_visible', isVisible);

  if (!isVisible) return null;

  async function handleUpgrade(targetTier) {
    if (upgradeInFlightRef.current) return;
    upgradeInFlightRef.current = true;
    setUpgrading(targetTier);
    setMsg(null);
    try {
      const res = await api.post(`/exploration/case/${caseId}/upgrade-tier`, {
        document_ids: docIds,
        target_tier: targetTier,
        approved: true,
      });
      const isBackground = res?.mode === 'background';
      if (isBackground && res.job_id) {
        // Hand off to the bg-job poller — `upgrading` and the ref stay
        // set so both buttons remain disabled until the job lands.
        // The poller clears the ref when bgJob transitions to a
        // terminal state.
        setBgJob({
          id: res.job_id,
          tier: targetTier,
          status: 'queued',
          totalDocs: docIds.length,
          elapsed: 0,
        });
        return;
      }
      // Inline mode: upgrade is already applied. Refetch the breakdown
      // so the prompt reflects the new state (typically hides itself).
      setMsg({
        text: targetTier === 'deep'
          ? '✓ Deep search ready — Arc can now find anything in these documents.'
          : '✓ Surface search ready — Arc can now search the sampled pages.',
        error: false,
      });
      await refetchBreakdown();
      setUpgrading(null);
      upgradeInFlightRef.current = false;
    } catch (err) {
      setMsg({ text: `Upgrade failed: ${err.message}`, error: true });
      setUpgrading(null);
      upgradeInFlightRef.current = false;
    }
  }

  // Live label for the deep button — doubles as the progress indicator
  // while a background upgrade runs. We only show "N docs" (not "X of Y")
  // because the complete_extraction handler doesn't surface a per-doc
  // counter mid-job; "elapsed" gives the user the time signal.
  function deepLabel() {
    if (bgJob && bgJob.tier === 'deep') {
      if (bgJob.status === 'completed') {
        const n = bgJob.completed ?? bgJob.totalDocs;
        return n
          ? `✓ Deep search ready · ${n} doc${n === 1 ? '' : 's'}`
          : '✓ Deep search ready';
      }
      if (bgJob.status === 'failed') {
        return '✗ Deep search failed — retry';
      }
      const n = bgJob.totalDocs || docIds.length;
      const elapsed = formatElapsed(bgJob.elapsed);
      return `🔎 Deep search · upgrading ${n} doc${n === 1 ? '' : 's'}${elapsed ? ` · ${elapsed}` : ''}`;
    }
    if (upgrading === 'deep') return 'Upgrading…';
    return `🔎 Deep search ($${deepCost.toFixed(2)})`;
  }

  function surfaceLabel() {
    if (bgJob && bgJob.tier === 'surface') {
      // Surface always runs inline today — defensive fallback only.
      return '🔍 Surface search · upgrading…';
    }
    if (upgrading === 'surface') return 'Upgrading…';
    return `🔍 Surface search ($${surfaceCost.toFixed(2)})`;
  }

  // Buttons stay disabled while either an inline upgrade is pending
  // or a background job is still working. They re-enable on bg
  // completion (briefly showing ✓) and on bg failure (so retry works
  // without a page refresh).
  const inFlight = (
    upgrading !== null ||
    (bgJob && bgJob.status !== 'completed' && bgJob.status !== 'failed')
  );

  return (
    <div className="tier-scope-prompt" ref={helpRef}>
      <div className="tier-scope-header">
        {total} document{total === 1 ? '' : 's'} in scope:
      </div>
      <ul className="tier-scope-counts">
        {skim > 0 && <li>• {skim} skim-only</li>}
        {surface > 0 && <li>• {surface} surface search</li>}
        {deep > 0 && <li>• {deep} deep search</li>}
      </ul>
      <div className="tier-scope-actions">
        {skim > 0 && surface > 0 && deepCost > 0 && !bgJob && (
          <span className="tier-scope-pitch">
            For complete coverage on every page:
          </span>
        )}
        {skim > 0 && (
          <button
            className="btn btn-sm btn-primary-ghost"
            onClick={() => handleUpgrade('surface')}
            disabled={inFlight}
          >
            {surfaceLabel()}
          </button>
        )}
        {(skim + surface) > 0 && (
          <button
            className="btn btn-sm btn-primary"
            onClick={() => handleUpgrade('deep')}
            disabled={inFlight}
          >
            {deepLabel()}
          </button>
        )}
      </div>
      {msg && (
        <div className={`tier-scope-msg ${msg.error ? 'error' : ''}`}>
          {msg.text}
        </div>
      )}
      {bgJob && bgJob.status === 'failed' && bgJob.error && (
        <div className="tier-scope-msg error">
          Deep search failed: {bgJob.error}
        </div>
      )}
    </div>
  );
}
